import { ChangeDetectorRef, Component, EventEmitter, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { DialogButton } from '@snapon/design-system-angular';
import { ApiService } from 'core/api/api.service';
import { AppInfoService } from 'core/app-info/app-info.service';
import { NotificationService } from 'core/notification/notification.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { countriesFromEurope } from 'core/app-info/country-specifics.enum';

@Component({
  selector: 'app-merge-lead-modal',
  templateUrl: './merge-lead-modal.component.html',
  styleUrls: ['./merge-lead-modal.component.scss'],
  standalone: false
})
export class MergeLeadModalComponent implements OnInit {
  appStrings = this.appInfoService.appStrings;
  form: UntypedFormGroup;
  accountOptions: { value: string, label: string, description: string }[] = [];
  selectedAccount: any = null;
  loading = false;
  isEuropeanLocale: boolean;
  confirmDialogOpen$ = new EventEmitter<boolean>();
  confirmDialogContent = '';
  confirmDialogButtons: DialogButton[] = [];

  private readonly urlAccountsByAm = '/api/customer/byAccountManager/';
  private readonly urlLeadDetail = '/api/lead/get/';
  private readonly urlDeliveryDetail = '/api/delivery/get/';
  private readonly urlMergeLead = '/api/lead/merge';
  private accountsById = new Map<string, any>();

  get record() {
    return this.data.record;
  }
  get isLead() {
    return this.record.recordType === 2;
  }

  constructor(
    public dialogRef: MatDialogRef<MergeLeadModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { record: any },
    private formBuilder: UntypedFormBuilder,
    private appInfoService: AppInfoService,
    private apiService: ApiService,
    private notificationService: NotificationService,
    private ref: ChangeDetectorRef
  ) { }

  ngOnInit() {
    const countryCode = this.appInfoService.userData?.UserInfo?.CountryCode ?? 'US';
    this.isEuropeanLocale = !!countriesFromEurope[countryCode];

    this.form = this.formBuilder.group({
      account: ['', Validators.required],
      acknowledge: [false]
    });

    this.form.controls.account.valueChanges.subscribe(value => {
      this.selectedAccount = value ? this.accountsById.get(value) ?? null : null;
    });

    this.loadAccounts();
  }

  private loadAccounts() {
    const detailUrl = this.isLead
      ? this.urlLeadDetail + this.record.leadId
      : this.urlDeliveryDetail + this.record.leadId;

    this.loading = true;
    this.apiService.get<any>(detailUrl).pipe(
      switchMap(detail => {
        const amId = detail?.accountManager?.id;
        if (!amId) {
          return of([]);
        }
        return this.apiService.get<any[]>(this.urlAccountsByAm + amId);
      }),
      finalize(() => {
        this.loading = false;
      })
    ).subscribe(accounts => {
      (accounts ?? []).forEach(a => {
        this.accountsById.set(String(a.id), a);
      });
      this.accountOptions = (accounts ?? []).map(a => ({
        value: String(a.id),
        label: a.name ?? '',
        description: ('BP#: ' + (a.bpNumber ?? '') + ', ' + (a.displayAddress ?? ''))
      }));
    });
  }

  get canMerge(): boolean {
    const accountSelected = !!this.selectedAccount;
    const acknowledged = this.isEuropeanLocale ? this.form.controls.acknowledge.value === true : true;
    return accountSelected && acknowledged;
  }

  merge() {
    if (!this.canMerge) {
      return;
    }

    const leadName = this.record.name1;
    const accountName = this.selectedAccount?.name ?? '';

    this.confirmDialogContent = this.appStrings.leads_mergeConfirmContent
      .replace('{0}', leadName)
      .replace('{1}', accountName);

    this.confirmDialogButtons = [
      {
        type: 'secondary',
        label: this.appStrings.button_cancel,
        onclick: () => { }
      },
      {
        type: 'primary',
        label: this.appStrings.leads_mergeButton,
        onclick: () => {
          this.executeMerge();
        }
      }
    ];

    this.ref.detectChanges();
    this.confirmDialogOpen$.emit(true);
  }

  private executeMerge() {
    this.loading = true;
    const leadId = this.record.leadId;
    const customerId = this.selectedAccount?.id;

    this.apiService.post(this.urlMergeLead, { customerId, leadId })
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: () => this.onMergeSuccess(),
        error: () => {
          this.loading = false;
        }
      });
  }

  private onMergeSuccess() {
    this.notificationService.showNotification(this.appStrings.leads_mergeSuccess, NotificationType.Success);
    this.dialogRef.close({ merged: true });
  }

  close() {
    this.dialogRef.close();
  }
}
